import { useState, useEffect } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

/**
 * Hook 用于检查当前用户是否是 Entity 的 Owner
 * 用于控制 Pipeline 执行按钮的权限
 * 
 * 当 permission.enabled 为 false 时，跳过检查，允许所有用户执行
 */
export const useOwnershipCheck = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const { entity } = useEntity();
  
  const [canExecute, setCanExecute] = useState<boolean>(false);
  const [ownerCheckDone, setOwnerCheckDone] = useState<boolean>(false);

  useEffect(() => {
    const checkOwnership = async () => {
      try {
        // 检查 permission 系统是否启用
        const permissionEnabled = configApi.getOptionalBoolean('permission.enabled') ?? false;
        
        // 如果 permission 系统未启用，直接允许执行
        if (!permissionEnabled) {
          setCanExecute(true);
          setOwnerCheckDone(true);
          return;
        }

        const identity = await identityApi.getBackstageIdentity();
        const userOwnershipRefs = identity.ownershipEntityRefs || [];
        const componentOwner = entity?.spec?.owner as string | undefined;

        if (!componentOwner) {
          // 没有 owner 定义，允许执行
          setCanExecute(true);
        } else {
          // 检查用户是否属于 owner 组
          // owner 格式: "group:default/invoice-owners"
          // userOwnershipRefs 格式: ["group:default/invoice-owners", "user:default/zhangwei"]
          const hasOwnership = userOwnershipRefs.some(ref =>
            ref === componentOwner ||
            ref.toLowerCase() === componentOwner.toLowerCase()
          );
          setCanExecute(hasOwnership);
        }
      } catch {
        // 检查失败时默认允许（降级处理）
        setCanExecute(true);
      } finally {
        setOwnerCheckDone(true);
      }
    };

    checkOwnership();
  }, [identityApi, configApi, entity]);

  return { canExecute, ownerCheckDone };
};
